import { useState, useEffect, useCallback } from "react";
import { getSupabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useDealerLog, LOG_ACTIONS } from "@/hooks/useDealerLog";

const AVAILABLE_CURRENCIES = [
  { code: "TRY", label: "TL", symbol: "$", flag: "🇹🇷" },
  { code: "USD", label: "USD", symbol: "$", flag: "🇺🇸" },
  { code: "EUR", label: "EUR", symbol: "€", flag: "🇪🇺" },
  { code: "GBP", label: "GBP", symbol: "£", flag: "🇬🇧" },
];

export default function DealerAdminCurrenciesPage() {
  const { user } = useAuth();
  const { logAction } = useDealerLog();
  const dealerId = user?.dealerId;

  const [dealerName, setDealerName] = useState("");
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>(["TRY"]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const fetchData = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    setError("");
    try {
      const supabase = getSupabase();
      const { data, error: err } = await supabase
        .from("dealers")
        .select("dealer_name, currencies")
        .eq("id", dealerId)
        .maybeSingle();

      if (err) throw err;
      if (data) {
        setDealerName(data.dealer_name || "");
        setSelectedCurrencies(data.currencies || ["TRY"]);
      }
    } catch (e: any) {
      setError(e.message || "Veri yüklenirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleCurrency = (code: string) => {
    setSelectedCurrencies((prev) => {
      if (prev.includes(code)) {
        if (prev.length === 1) return prev;
        return prev.filter((c) => c !== code);
      }
      return [...prev, code];
    });
  };

  const saveCurrencies = async () => {
    if (!dealerId || selectedCurrencies.length === 0) return;
    setSaving(true);
    setError("");
    setSuccessMsg("");
    try {
      const supabase = getSupabase();
      const { error: err } = await supabase
        .from("dealers")
        .update({ currencies: selectedCurrencies })
        .eq("id", dealerId);

      if (err) throw err;

      setSuccessMsg("Para birimleri güncellendi.");
      logAction(dealerId, user?.id || null, LOG_ACTIONS.CURRENCY_UPDATE, `Para birimleri güncellendi: ${selectedCurrencies.join(", ")}`);
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (e: any) {
      setError(e.message || "Güncellenirken hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  const getCurrencyLabel = (code: string) => {
    const found = AVAILABLE_CURRENCIES.find((c) => c.code === code);
    return found ? `${found.flag} ${found.label} (${found.symbol})` : code;
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-foreground-950"><a id="dealer-admin-currencies">Para Birimi</a></h4>
        <p className="text-sm text-foreground-500 mt-0.5">Bayiniz için geçerli para birimlerini belirleyin</p>
      </div>

      {error && (
        <div className="bg-accent-100 border border-accent-300 text-accent-900 rounded-xl px-4 py-3 mb-4 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
          <button onClick={() => setError("")} className="ml-auto text-accent-700 font-medium cursor-pointer">Kapat</button>
        </div>
      )}

      {successMsg && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-4 py-3 mb-4 text-sm flex items-center gap-2">
          <i className="ri-check-line"></i>
          {successMsg}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
          <div className="mb-4">
            <p className="text-sm font-medium text-foreground-900">{dealerName}</p>
            <p className="text-xs text-foreground-400 mt-0.5">Geçerli para birimlerini seçin (en az bir tane)</p>
          </div>

          <div className="space-y-2">
            {AVAILABLE_CURRENCIES.map((curr) => {
              const isSelected = selectedCurrencies.includes(curr.code);
              return (
                <button
                  key={curr.code}
                  onClick={() => toggleCurrency(curr.code)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all cursor-pointer ${isSelected ? "bg-accent-100 border border-accent-200 text-accent-700" : "bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200/70"}`}
                >
                  <span className="text-lg">{curr.flag}</span>
                  <span className="flex-1 text-left">{curr.label}</span>
                  <span className="text-foreground-400 font-mono">{curr.symbol}</span>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${isSelected ? "border-accent-500 bg-accent-500" : "border-background-300/60"}`}>
                    {isSelected && <i className="ri-check-line text-background-50 text-xs"></i>}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex gap-2">
            <button
              onClick={saveCurrencies}
              disabled={saving || selectedCurrencies.length === 0}
              className="flex-1 py-2.5 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}